import React from 'react';
import { useIsMobileDimension } from '@carnica/utils/hooks';

import { AccumServiceType, EnrichedAccumItemType } from 'types/accumulators';
import {
  formatTimeUnit,
  getMainTextColor,
  getMainTextVariant,
  getSecondaryTextVariant,
  getUnlimitedActiveEnabled,
  isAdditional,
} from 'utils/accumulators';
import { elkPlanBUnlimAccum3HoursEnabled, getIsTariffAccumsFttbDivideEnabled } from 'utils';
import { getUnlimitedVariant } from 'utils/accumulators/getUnlimitedVariant';

import { UnlimitedAccumulatorTextContent } from '../UnlimitedAccumulatorTextContent/UnlimitedAccumulatorTextContent';
import { BaseTextContent } from '../BaseTextContent';
import { GiftedAccumulator } from '../GiftedAccumulator';
import { UnlimitedForDay } from '../UnlimitedAccumulatorTextContent/UnlimitedForDay';
import { UnlimitedWithLimitedTethering } from '../UnlimitedAccumulatorTextContent/UnlimitedWithLimitedTethering';
import { UnlimitedWithUnlimitedTethering } from '../UnlimitedAccumulatorTextContent/UnlimitedWithUnlimitedTethering';
import {
  getMainTextForUnlimitedTethering,
  getSecondaryTextForUnlimitedTethering,
  getTextColorForUnlimitedForDay,
  getTextForUnlimitedForDay,
  getTextForUnlimitedWithLimitedTethering,
  getTextValueWithRest,
} from '../UnlimitedAccumulatorTextContent/utils';
import { UnlimitedThreeHoursActive } from '../UnlimitedAccumulatorTextContent/UnlimitedThreeHoursActive';

interface IProps {
  accumulator: EnrichedAccumItemType;
  isInline?: boolean;
  isMobile?: boolean;
  onMetricView?: (data: string) => void;
  services?: AccumServiceType;
  tetheringIsLow?: boolean;
  userType: string;
}

const ADDITIONAL = 'доп';

export const BaseTariff: React.FC<IProps> = ({
  accumulator,
  services,
  isInline = false,
  isMobile = false,
  tetheringIsLow = false,
  userType,
  onMetricView,
}) => {
  const {
    rest,
    displayUnit,
    isLow = false,
    isUnlimited = false,
    size,
    isGiftBucket = false,
    tetheringRest = 0,
    tetheringSize = 0,
    isLowTethering = false,
  } = accumulator;
  const unit = formatTimeUnit(displayUnit?.toLowerCase() ?? '');
  const isMobileDimension = useIsMobileDimension();
  const isMobileSize = isMobile || isMobileDimension;

  const getSecondaryText = () => {
    if (typeof size === 'number' && !Number.isNaN(size)) {
      return isAdditional(accumulator) ?
          `из ${getTextValueWithRest(size)} ${ADDITIONAL}`
        : `из ${getTextValueWithRest(size)}`;
    }
    return isAdditional(accumulator) ? `${ADDITIONAL}` : undefined;
  };
  const textColor = getMainTextColor(isLow);
  const isTariffDivideEnabled = getIsTariffAccumsFttbDivideEnabled();
  const isStandardUnlimited = !isGiftBucket && isUnlimited;
  const unlimitedAccumulatorVariant = getUnlimitedVariant({
    accumulator,
    services,
    tetheringIsLow,
  });
  const isElkUnlimAccum3HoursEnabled = elkPlanBUnlimAccum3HoursEnabled();
  const isUnlimitedActiveEnabled = getUnlimitedActiveEnabled({
    isStandardUnlimited,
    isTariffDivideEnabled,
    isElkUnlimAccum3HoursEnabled,
    services,
  });

  return (
    <>
      <UnlimitedAccumulatorTextContent
        mainText={unit}
        mainTextVariant={getMainTextVariant(false, isMobileSize)}
        onMetricView={onMetricView}
        shouldRender={
          isStandardUnlimited &&
          (isTariffDivideEnabled ? unlimitedAccumulatorVariant === 'UnlimitedBase' : true)
        }
        userType={userType}
      />
      <UnlimitedForDay
        onMetricView={onMetricView}
        shouldRender={
          isStandardUnlimited &&
          isTariffDivideEnabled &&
          unlimitedAccumulatorVariant === 'Unlimited24Hours'
        }
        text={getTextForUnlimitedForDay(services?.unlimited24?.expirationDate)}
        textColor={getTextColorForUnlimitedForDay(services?.unlimited24?.expirationDate)}
        textVariant={getMainTextVariant(false, isMobileSize)}
        userType={userType}
      />
      <UnlimitedThreeHoursActive
        onMetricView={onMetricView}
        shouldRender={isUnlimitedActiveEnabled}
        text={getTextForUnlimitedForDay(services?.unlimited3?.expirationDate)}
        textVariant={getMainTextVariant(false, isMobileSize)}
        userType={userType}
      />
      <UnlimitedWithLimitedTethering
        onMetricView={onMetricView}
        shouldRender={
          isStandardUnlimited &&
          isTariffDivideEnabled &&
          unlimitedAccumulatorVariant === 'UnlimitedWithLimitedTethering'
        }
        text={getTextForUnlimitedWithLimitedTethering({ rest: tetheringRest, size: tetheringSize })}
        textColor={isLowTethering ? 'error' : 'secondary'}
        textVariant={getMainTextVariant(false, isMobileSize)}
        userType={userType}
      />
      <UnlimitedWithUnlimitedTethering
        mainText={getMainTextForUnlimitedTethering({
          tetheringExpiration: services?.tethering?.tetheringProExpiration,
          tetheringIsLow,
          tetheringProMode: services?.tethering?.tetheringProMode,
        })}
        mainTextColor={
          !services?.tethering?.tetheringProMode && tetheringIsLow ? 'error' : 'secondary'
        }
        onMetricView={onMetricView}
        secondaryText={getSecondaryTextForUnlimitedTethering({
          tetheringIsLow,
          tetheringProMode: services?.tethering?.tetheringProMode,
        })}
        shouldRender={
          isStandardUnlimited &&
          isTariffDivideEnabled &&
          unlimitedAccumulatorVariant === 'UnlimitedWithUnlimitedTethering'
        }
        textVariant={getMainTextVariant(false, isMobileSize)}
        userType={userType}
      />
      <BaseTextContent
        isUnlimitedEnabled={['Unlimited3HoursAvailable', 'Unlimited3HoursCooling']?.includes(
          unlimitedAccumulatorVariant,
        )}
        mainText={`${getTextValueWithRest(rest)} ${unit}`}
        mainTextColor={textColor}
        mainTextVariant={getMainTextVariant(isInline, isMobileSize)}
        onMetricView={onMetricView}
        secondaryText={getSecondaryText()}
        secondaryTextColor={isTariffDivideEnabled ? 'primary' : textColor}
        secondaryTextVariant={getSecondaryTextVariant(isMobileSize)}
        shouldRender={!isGiftBucket && !isUnlimited}
        userType={userType}
      />
      <GiftedAccumulator
        accumulator={accumulator}
        isInline={isInline}
        isMobile={isMobileSize}
        shouldRender={isGiftBucket}
      />
    </>
  );
};
